// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/TriggerAttachFlow.jsx
import React, { useMemo, useState, useCallback } from "react";
import { FaPlus, FaEdit, FaBolt } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import {
  classifyAttachment,
  sortIntoBuckets,
  buildAttachmentFromClassification,
} from "./triggerEligibility";
import { normaliseTriggerAttachment } from "./triggerAttachment";
import TriggerFieldMappingModal from "./TriggerFieldMappingModal";
import ConnectorDefinitionPickerModal from "./ConnectorDefinitionPickerModal";
import styles from "./ParallelActionsPanel.module.css";

function enrichDefinition(definition, operationsByName) {
  const op = operationsByName[definition.operation_name];
  if (!op) return { ...definition, input_schema: {} };
  return { ...definition, input_schema: op.input_schema || {} };
}

export default function TriggerAttachFlow({
  isOpen,
  onClose,
  siteFields,
  connectedProviders,
  definitions,
  operations,
  existingAttachments = [], // ⭐ NEW
  onAttach,
  initialAttachment = null,
}) {
  const operationsByName = useMemo(() => {
    const out = {};
    for (const op of operations || []) out[op.name] = op;
    return out;
  }, [operations]);

  // ⭐ Filter out definitions that are already attached
  const availableDefinitions = useMemo(() => {
    const attachedIds = new Set(existingAttachments.map(t => t.connector_definition_id));
    return (definitions || []).filter(d => !attachedIds.has(d.definition_id));
  }, [definitions, existingAttachments]);

  const enrichedDefinitions = useMemo(
    () => availableDefinitions.map((d) => enrichDefinition(d, operationsByName)),
    [availableDefinitions, operationsByName],
  );

  const buckets = useMemo(
    () => sortIntoBuckets(enrichedDefinitions, siteFields, connectedProviders),
    [enrichedDefinitions, siteFields, connectedProviders],
  );

  const [pickedDefinition, setPickedDefinition] = useState(null);
  const [pickedClassification, setPickedClassification] = useState(null);

  const handlePickerPick = useCallback(
    (entry) => {
      setPickedDefinition(entry.definition);
      setPickedClassification(entry.classification);
    },
    [],
  );

  const handleMappingSave = useCallback(
    (attachment) => {
      const normalised = normaliseTriggerAttachment(attachment);
      if (!normalised) {
        console.error("[TriggerAttachFlow] malformed attachment", attachment);
        return;
      }
      onAttach(normalised);
      setPickedDefinition(null);
      setPickedClassification(null);
      onClose();
    },
    [onAttach, onClose],
  );

  const handleMappingClose = useCallback(() => {
    setPickedDefinition(null);
    setPickedClassification(null);
  }, []);

  if (!isOpen) return null;

  if (!pickedDefinition) {
    return (
      <ConnectorDefinitionPickerModal
        isOpen={true}
        onClose={onClose}
        buckets={buckets}
        onPick={handlePickerPick}
      />
    );
  }

  return (
    <TriggerFieldMappingModal
      isOpen={true}
      onClose={handleMappingClose}
      definition={pickedDefinition}
      classification={pickedClassification}
      siteFields={siteFields}
      onAttach={handleMappingSave}
      initialAttachment={initialAttachment}
    />
  );
}

export function TriggerAttachmentList({
  attachments,
  definitionsById,
  siteFields,
  operations,
  onChange,
  onAdd,
  readOnly = false,
}) {
  const safeAttachments = Array.isArray(attachments) ? attachments : [];
  const [editingAttachmentIndex, setEditingAttachmentIndex] = useState(null);

  const operationsByName = useMemo(() => {
    const out = {};
    for (const op of operations || []) out[op.name] = op;
    return out;
  }, [operations]);

  const handleRemove = (idx) => {
    const next = safeAttachments.filter((_, i) => i !== idx);
    onChange(next);
  };

  const editingAttachment =
    editingAttachmentIndex != null
      ? safeAttachments[editingAttachmentIndex]
      : null;
  const editingDefinition = editingAttachment
    ? definitionsById[editingAttachment.connector_definition_id]
    : null;

  const enrichedEditingDefinition = useMemo(() => {
    if (!editingDefinition) return null;
    return enrichDefinition(editingDefinition, operationsByName);
  }, [editingDefinition, operationsByName]);

  const enrichedEditingClassification = useMemo(() => {
    if (!enrichedEditingDefinition) return null;
    return classifyAttachment(enrichedEditingDefinition, siteFields, []);
  }, [enrichedEditingDefinition, siteFields]);

  const handleEditSave = (attachment) => {
    const normalised = normaliseTriggerAttachment(attachment);
    if (!normalised) {
      console.error("[TriggerAttachmentList] malformed attachment on edit", attachment);
      return;
    }
    const next = safeAttachments.map((t, i) =>
      i === editingAttachmentIndex ? normalised : t,
    );
    onChange(next);
    setEditingAttachmentIndex(null);
  };

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div className={styles.panelHeaderLeft}>
          <FaBolt className={styles.panelIcon} size={14} />
          <span className={styles.panelTitle}>Parallel Actions</span>
          <span className={styles.panelSubtitle}>fire-and-forget</span>
        </div>
        {!readOnly && (
          <button type="button" className={styles.addButton} onClick={onAdd}>
            <FaPlus size={11} /> Add parallel action
          </button>
        )}
      </div>

      <p className={styles.panelHelp}>
        These run in the background when this node's API returns. They never
        block the chain or affect its outcome — a failure here is logged but
        doesn't stop the flow.
      </p>

      {safeAttachments.length === 0 && (
        <div className={styles.emptyState}>
          No parallel actions attached. Click <strong>Add parallel action</strong>{" "}
          to schedule an operation to fire alongside this step.
        </div>
      )}

      {safeAttachments.length > 0 && (
        <div className={styles.triggerList}>
          {safeAttachments.map((attachment, idx) => {
            const def = definitionsById[attachment.connector_definition_id];
            const overrideCount = Object.keys(
              attachment.field_overrides || {},
            ).length;

            return (
              <div key={idx} className={styles.triggerRow}>
                <div className={styles.triggerBody}>
                  <div className={styles.triggerName}>
                    {def ? def.name : "(missing definition)"}
                  </div>
                  <div className={styles.triggerMeta}>
                    <code className={styles.triggerOpName}>
                      {def
                        ? def.operation_name
                        : attachment.connector_definition_id}
                    </code>
                    {overrideCount > 0 && (
                      <span className={styles.triggerDesc}>
                        {" · "}
                        {overrideCount} field override
                        {overrideCount === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                </div>

                {!readOnly && (
                  <div className={styles.triggerActions}>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => setEditingAttachmentIndex(idx)}
                      title="Edit field mapping"
                    >
                      <FaEdit size={13} />
                    </button>
                    <button
                      type="button"
                      className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                      onClick={() => handleRemove(idx)}
                      title="Remove this parallel action"
                    >
                      <RiDeleteBin6Line size={13} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editingAttachment && enrichedEditingDefinition && (
        <TriggerFieldMappingModal
          isOpen={true}
          onClose={() => setEditingAttachmentIndex(null)}
          definition={enrichedEditingDefinition}
          classification={enrichedEditingClassification}
          siteFields={siteFields}
          onAttach={handleEditSave}
          initialAttachment={editingAttachment}
        />
      )}
    </div>
  );
}